/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {getAccount} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, AccountLink, CRUDPermissions} from '@ion/core/src/types/entities';
import {CTC_APPLICATIONS} from '@ion/dls/src/editors/GroupEditorUtils';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {AccountEditor} from './AccountEditor';
import {addAdminGroup, addGroup} from './state';

export interface AddAccountProps {
  includeIonAccount?: boolean;
  rootAccountId?: number;
}

export interface AddGroupEditorProps extends AddAccountProps {
  refreshInitialization?: Function;
  defaultAccount?: AccountLink;
  isCtcAdmin: boolean;
  isViewDialog: boolean;
  includeApplications?: boolean;
  accountsPermissions?: CRUDPermissions;
  applicationPermissions?: CRUDPermissions;
  hasCustomVisionPermission?: boolean;
}

/**
 * Display Add Account Editor
 */
export const AddGroupEditor = ({
  isCtcAdmin,
  isViewDialog,
  includeApplications,
  defaultAccount,
  refreshInitialization,
  accountsPermissions,
  applicationPermissions,
  includeIonAccount = false,
  hasCustomVisionPermission = false
}: AddGroupEditorProps) => {
  const dispatch                          = useDispatch();
  const {globalGroup}                     = useSelector(({globalGroup}) => globalGroup);
  const {result: created, loading}        = useSelector(({adminGroups: {add}}) => add);
  const {account, timeZone}               = useAuthenticatedUser();
  const [clearForm, setClearForm]         = useState(false);
  const [isCreateError, setIsCreateError] = useState(false);
  const isView                            = isViewDialog || false;
  const rootAccountId                     = Number(get('rootAccountId'));

  useEffect(() => {
    if (created) {
      setClearForm(true);
      dispatch(addAdminGroup(false));
    }
  }, [created]);

  const checkIonAccount = useCallback((group: Account) => {
    const globalAccountId = entityId(globalGroup?.href);
    let app = [];
    if (globalAccountId !== rootAccountId && !group.enterprise) {
      (async function() {
        const accountData = await getAccount(globalAccountId);
        app = accountData?.applications?.filter(app => app.href.includes(CTC_APPLICATIONS.CACTC));
        if (app?.length) {
          setIsCreateError(true);
        } else {
          addNewGroup(group);
        }
      })();
    } else {
      addNewGroup(group);
    }
  }, [globalGroup]);

  const addNewGroup = useCallback((group: Account) => {
    dispatch(addGroup(group, isCtcAdmin, refreshInitialization));
  }, [isCtcAdmin, refreshInitialization, globalGroup]);

  const onCloseDialog = useCallback(() => {
    dispatch(addAdminGroup(false));
  }, []);

  const onDismiss = useCallback(() => {
    setIsCreateError(false);
  }, []);

  return (
    <>
      <AccountEditor open
        account={globalGroup || account}
        defaultAccount={defaultAccount}
        isViewDialog={isView}
        includeApplications={includeApplications}
        includeIonAccount={includeIonAccount}
        rootAccountId={entityId(account)}
        displayApplicationName={isCtcAdmin}
        timeZone={timeZone}
        accountsPermissions={accountsPermissions}
        applicationPermissions={applicationPermissions}
        hasCustomVisionPermission={hasCustomVisionPermission}
        onSubmit={!includeIonAccount ? checkIonAccount : addNewGroup}
        onCloseDialog={onCloseDialog}
        clear={clearForm}
        loading={loading} />
      {isCreateError && (
        <UserNotificationDialog show
          capitalize
          title={translate('admin-groups:edit.bulk.information')}
          message={translate('admin-groups:dialog.errors.cactcError')}
          dismissLabel={translate('common:ok')}
          onDismiss={onDismiss} />
      )}
    </>
  );
};

export default AddGroupEditor;
